import { useEffect, useRef, useState } from 'react';
import { FieldErrors, FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form';
import { ACCEPTED_FILE_TYPES, IdentityType, MAX_FILE_SIZE_BYTES } from '@brighte/validation';
import { css } from '@emotion/css';
import {
  ApplicationDocumentType,
  ApplicationEntityType,
  useCreateApplicationDocumentMutation,
  useUpdateApplicationDocumentStatusUpdatedMutation,
} from '@graphql/generated/graphql';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Dropzone, DropzoneProps } from '@spark-web/dropzone';
import { Field } from '@spark-web/field';
import { Text } from '@spark-web/text';
import { useResizeDocumentForUpload, useUploadFilesToS3 } from '../../hooks';
import UploadedDocumentsLink from './UploadedDocumentsLink';

type FileWithPreview = File & {
  preview: string;
  path: string;
  id: string;
};

type Props<TFieldValues extends FieldValues> = {
  accept?: DropzoneProps['accept'];
  applicationId: string;
  title: string;
  description?: string;
  fieldName: Path<TFieldValues>;
  onFileSubmit?: () => void;
  setFieldValue: UseFormSetValue<TFieldValues>;
  documentType: ApplicationDocumentType;
  existingUploadedDocuments?: Array<{ documentId: string; fileName: string }>;
  applicantType: ApplicationEntityType;
  maxFiles?: number;
  multiple?: boolean;
  error?: FieldErrors<IdentityType>['documents'];
  onVerificationStart?: (uploadedDocuments: Array<{ documentId: string; fileName: string }>) => void;
  onVerificationError?: () => void;
  isVerificationFailed?: boolean;
  isUploadDisabled?: boolean;
  submitLabel?: string;
  allowSubmitWhenNoSelection?: boolean;
  onManualContinue?: () => void;
  onUploadStateChange?: (isUploading: boolean) => void;
};

export function FileUpload<TFieldValues extends FieldValues = FieldValues>({
  applicationId,
  title,
  description,
  fieldName,
  accept = ACCEPTED_FILE_TYPES,
  existingUploadedDocuments = [],
  setFieldValue,
  onFileSubmit,
  documentType,
  applicantType,
  maxFiles = 0,
  multiple = true,
  error,
  onVerificationStart,
  onVerificationError,
  isVerificationFailed = false,
  isUploadDisabled = false,
  submitLabel,
  allowSubmitWhenNoSelection = false,
  onManualContinue,
  onUploadStateChange,
}: Readonly<Props<TFieldValues>>) {
  const [uploadError, setUploadError] = useState<string | undefined>();
  const disabledDropzoneClassName = css({
    pointerEvents: 'none',
    opacity: 0.5,
  });

  const { mutateAsync: createUploadDocument } = useCreateApplicationDocumentMutation({ useErrorBoundary: true });
  const { mutateAsync: uploadDocument } = useUploadFilesToS3();
  const resizeDocumentForUpload = useResizeDocumentForUpload();
  const { mutateAsync: updateDocumentStatus } = useUpdateApplicationDocumentStatusUpdatedMutation({
    useErrorBoundary: true,
  });

  const [selectedDocument, setSelectedDocument] = useState<Array<FileWithPreview>>([]);
  const [uploadedDocument, setUploadedDocument] = useState<Array<{ documentId: string; fileName: string }>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const uploadedDocumentRef = useRef(uploadedDocument);
  uploadedDocumentRef.current = uploadedDocument;

  useEffect(() => {
    onUploadStateChange?.(loading);
  }, [loading, onUploadStateChange]);
  useEffect(() => {
    setUploadedDocument(existingUploadedDocuments);
  }, [existingUploadedDocuments]);

  const handleRemoveDocument = (documentId: string) => {
    const updatedDocuments = uploadedDocumentRef.current.filter(document => document.documentId !== documentId);
    uploadedDocumentRef.current = updatedDocuments;
    setUploadedDocument(updatedDocuments);
    setFieldValue(fieldName, updatedDocuments as PathValue<TFieldValues, Path<TFieldValues>>);
  };

  const handleRestoreDocument = (documentId: string, fileName: string) => {
    if (uploadedDocumentRef.current.some(document => document.documentId === documentId)) {
      return;
    }
    const updatedDocuments = [...uploadedDocumentRef.current, { documentId, fileName }];
    uploadedDocumentRef.current = updatedDocuments;
    setUploadedDocument(updatedDocuments);
    setFieldValue(fieldName, updatedDocuments as PathValue<TFieldValues, Path<TFieldValues>>);
  };

  const uploadFiles = async (file: FileWithPreview) => {
    // Large files are compressed by the backend before upload; small files pass through.
    const uploadFile = await resizeDocumentForUpload(file);

    //saves the selected file to DB and fetches the presigned url
    const data = await createUploadDocument({
      createApplicationDocumentInput: {
        applicationId,
        filename: file.path,
        fileSize: uploadFile.size,
        type: documentType,
        entityType: applicantType,
      },
    });

    //If the file is successfully saved to DB, then upload the file to S3
    const {
      createApplicationDocument: { document, url, fieldsJson },
    } = data || {};

    if (!url || !fieldsJson) {
      setUploadError(`Failed to upload ${file.name}`);
      throw new Error(`Failed to get presigned post url for file ${file.name}`);
    }
    //Constructs the form data to upload the file to S3
    const uploadMetaData = JSON.parse(fieldsJson);
    const formData = new FormData();
    Object.keys(uploadMetaData).forEach(key => {
      formData.append(key, uploadMetaData[key]);
    });

    formData.append('file', uploadFile);

    //Utilizes the presigned url to upload the file to S3
    await uploadDocument({ bucketUrl: url, file: formData });

    //Updates the document status to uploaded once S3 upload is successful
    await updateDocumentStatus({
      updateApplicationDocumentStatusUpdatedInput: {
        documentId: document.id,
        applicationId: applicationId,
      },
    });

    return {
      documentId: document.id,
      fileName: file.name,
    };
  };

  const handleUpload = async () => {
    if (onManualContinue) {
      onManualContinue();
      return;
    }
    setUploadError(undefined);
    setLoading(true);
    await Promise.all(selectedDocument.map(file => uploadFiles(file)))
      .then(response => {
        const allDocuments = [...uploadedDocumentRef.current, ...response];
        uploadedDocumentRef.current = allDocuments;
        setUploadedDocument(allDocuments);
        setFieldValue(fieldName, allDocuments as PathValue<TFieldValues, Path<TFieldValues>>);
        if (onFileSubmit) {
          onFileSubmit();
        }
        onVerificationStart?.(allDocuments);
      })
      .catch(() => {
        setUploadError(`Failed to upload file`);
        onVerificationError?.();
      });
    setLoading(false);
  };
  const isFileTypeAccepted = (file: File): boolean => {
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    return Object.entries(accept).some(([mimeType, extensions]) => {
      if (extensions.length > 0) {
        return extensions.includes(fileExtension);
      }
      if (mimeType.endsWith('/*')) {
        return file.type.startsWith(mimeType.replace('/*', '/'));
      }
      return file.type === mimeType;
    });
  };

  const handleFileDrop = (event: { target: { value: unknown } }) => {
    const value = event?.target?.value;
    const files = Array.isArray(value) ? (value as Array<FileWithPreview>) : [];
    const invalidFiles = files.filter(file => !isFileTypeAccepted(file));
    if (invalidFiles.length > 0) {
      const extensions = Object.values(accept).flat().join(', ');
      setUploadError(`Unsupported file format. Please upload a file in one of the following formats: ${extensions}`);
      setSelectedDocument([]);
      return;
    }
    if (maxFiles > 0 && files.length > maxFiles) {
      setUploadError(`You can only upload up to ${maxFiles} files at once. Please remove a file and try again.`);
      setSelectedDocument([]);
      return;
    }
    setUploadError(undefined);
    setSelectedDocument(files);
  };

  return (
    <>
      <Field label={isVerificationFailed ? '' : title}>
        {description ? <Text>{description}</Text> : null}
        {error || uploadError ? <Alert tone="critical">{error?.message || uploadError}</Alert> : null}
        <Box aria-disabled={isUploadDisabled} className={isUploadDisabled ? disabledDropzoneClassName : undefined}>
          <Dropzone
            accept={accept}
            onChange={isUploadDisabled ? undefined : handleFileDrop}
            maxFileSizeKb={Math.floor(MAX_FILE_SIZE_BYTES / 1024)}
            maxFiles={0}
            multiple={multiple}
          />
        </Box>
        {maxFiles > 0 ? (
          <Text size="xsmall" tone="muted">
            You can upload up to {maxFiles} files at once.
          </Text>
        ) : null}
        {existingUploadedDocuments &&
          uploadedDocument?.length > 0 &&
          uploadedDocument.map(({ documentId, fileName }) => (
            <UploadedDocumentsLink
              key={documentId}
              applicationId={applicationId}
              fileName={fileName}
              documentId={documentId}
              onRemoveDocument={handleRemoveDocument}
              onRestoreDocument={handleRestoreDocument}
            />
          ))}
      </Field>

      <Box display="flex" justifyContent="center" marginTop="small">
        <Button
          onClick={handleUpload}
          disabled={(!allowSubmitWhenNoSelection && selectedDocument.length === 0 && !onManualContinue) || loading}
          loading={loading}
        >
          {submitLabel ?? 'Upload documents'}
        </Button>
      </Box>
    </>
  );
}
